import { TagIcon } from "@heroicons/react/24/outline"
import { InitiativeType } from "~/ui-rtk/api/types/custom-attribution-enums"

/**
 * Full set of Tailwind class strings an initiative-type column/tab needs.
 * Tailwind can only see fully-static class names, so every assignable color
 * family must have a complete, literal entry here — no dynamic interpolation.
 */
export interface ColorConfig {
  bg: string
  border: string
  hoverBg: string
  hoverBorder: string
  accent: string
  button: string
  emptyBg: string
  emptyIcon: string
  tabActive: string
  tabHover: string
}

const SHARED_BG = "from-system-blue-800/90 to-system-blue-900/70"
const SHARED_HOVER_BG =
  "hover:from-system-blue-800/95 hover:to-system-blue-900/80"

/**
 * Static palette keyed by color family. Predefined-type families reproduce the
 * exact strings the board/tabs used before; custom families use valid theme
 * families (see ASSIGNABLE_COLOR_FAMILIES in the API service).
 */
export const INITIATIVE_TYPE_PALETTE: Record<string, ColorConfig> = {
  "system-success": {
    bg: SHARED_BG,
    border: "border-system-success-600/40",
    hoverBg: SHARED_HOVER_BG,
    hoverBorder: "hover:border-system-success-500/60",
    accent: "text-system-success-300",
    button:
      "bg-system-success-500/20 hover:bg-system-success-500/30 border-system-success-400/30 hover:border-system-success-400/50 text-system-success-300",
    emptyBg:
      "from-system-success-500/5 to-system-success-600/3 border-system-success-600/50",
    emptyIcon: "bg-system-success-500/10 text-system-success-400",
    tabActive:
      "text-system-success-300 border-b-2 border-system-success-400 bg-system-success-500/10",
    tabHover: "hover:text-system-success-300 hover:bg-system-success-500/5",
  },
  "system-cyan": {
    bg: SHARED_BG,
    border: "border-system-cyan-500/40",
    hoverBg: SHARED_HOVER_BG,
    hoverBorder: "hover:border-system-cyan-400/60",
    accent: "text-system-cyan-200",
    button:
      "bg-system-cyan-400/20 hover:bg-system-cyan-400/30 border-system-cyan-300/30 hover:border-system-cyan-300/50 text-system-cyan-200",
    emptyBg:
      "from-system-cyan-400/5 to-system-cyan-500/3 border-system-cyan-500/50",
    emptyIcon: "bg-system-cyan-400/10 text-system-cyan-300",
    tabActive:
      "text-system-cyan-200 border-b-2 border-system-cyan-400 bg-system-cyan-500/10",
    tabHover: "hover:text-system-cyan-200 hover:bg-system-cyan-500/5",
  },
  "system-magenta": {
    bg: SHARED_BG,
    border: "border-system-magenta-500/40",
    hoverBg: SHARED_HOVER_BG,
    hoverBorder: "hover:border-system-magenta-400/60",
    accent: "text-system-magenta-200",
    button:
      "bg-system-magenta-400/20 hover:bg-system-magenta-400/30 border-system-magenta-300/30 hover:border-system-magenta-300/50 text-system-magenta-200",
    emptyBg:
      "from-system-magenta-400/5 to-system-magenta-500/3 border-system-magenta-500/50",
    emptyIcon: "bg-system-magenta-400/10 text-system-magenta-300",
    tabActive:
      "text-system-magenta-200 border-b-2 border-system-magenta-400 bg-system-magenta-500/10",
    tabHover: "hover:text-system-magenta-200 hover:bg-system-magenta-500/5",
  },
  "system-sea": {
    bg: SHARED_BG,
    border: "border-system-sea-600/40",
    hoverBg: SHARED_HOVER_BG,
    hoverBorder: "hover:border-system-sea-500/60",
    accent: "text-system-sea-300",
    button:
      "bg-system-sea-500/20 hover:bg-system-sea-500/30 border-system-sea-400/30 hover:border-system-sea-400/50 text-system-sea-300",
    emptyBg:
      "from-system-sea-500/5 to-system-sea-600/3 border-system-sea-600/50",
    emptyIcon: "bg-system-sea-500/10 text-system-sea-400",
    tabActive:
      "text-system-sea-300 border-b-2 border-system-sea-400 bg-system-sea-500/10",
    tabHover: "hover:text-system-sea-300 hover:bg-system-sea-500/5",
  },
  "system-orange": {
    bg: SHARED_BG,
    border: "border-system-orange-500/40",
    hoverBg: SHARED_HOVER_BG,
    hoverBorder: "hover:border-system-orange-400/60",
    accent: "text-system-orange-200",
    button:
      "bg-system-orange-400/20 hover:bg-system-orange-400/30 border-system-orange-300/30 hover:border-system-orange-300/50 text-system-orange-200",
    emptyBg:
      "from-system-orange-400/5 to-system-orange-500/3 border-system-orange-500/50",
    emptyIcon: "bg-system-orange-400/10 text-system-orange-300",
    tabActive:
      "text-system-orange-200 border-b-2 border-system-orange-400 bg-system-orange-500/10",
    tabHover: "hover:text-system-orange-200 hover:bg-system-orange-500/5",
  },
  "system-yellow": {
    bg: SHARED_BG,
    border: "border-system-yellow-500/40",
    hoverBg: SHARED_HOVER_BG,
    hoverBorder: "hover:border-system-yellow-400/60",
    accent: "text-system-yellow-200",
    button:
      "bg-system-yellow-400/20 hover:bg-system-yellow-400/30 border-system-yellow-300/30 hover:border-system-yellow-300/50 text-system-yellow-200",
    emptyBg:
      "from-system-yellow-400/5 to-system-yellow-500/3 border-system-yellow-500/50",
    emptyIcon: "bg-system-yellow-400/10 text-system-yellow-300",
    tabActive:
      "text-system-yellow-200 border-b-2 border-system-yellow-400 bg-system-yellow-500/10",
    tabHover: "hover:text-system-yellow-200 hover:bg-system-yellow-500/5",
  },
  "system-indigo": {
    bg: SHARED_BG,
    border: "border-system-indigo-600/40",
    hoverBg: SHARED_HOVER_BG,
    hoverBorder: "hover:border-system-indigo-500/60",
    accent: "text-system-indigo-200",
    button:
      "bg-system-indigo-500/20 hover:bg-system-indigo-500/30 border-system-indigo-400/30 hover:border-system-indigo-400/50 text-system-indigo-300",
    emptyBg:
      "from-system-indigo-500/5 to-system-indigo-600/3 border-system-indigo-600/50",
    emptyIcon: "bg-system-indigo-500/10 text-system-indigo-400",
    tabActive:
      "text-system-indigo-200 border-b-2 border-system-indigo-400 bg-system-indigo-500/10",
    tabHover: "hover:text-system-indigo-200 hover:bg-system-indigo-500/5",
  },
  "system-violet": {
    bg: SHARED_BG,
    border: "border-system-violet-600/40",
    hoverBg: SHARED_HOVER_BG,
    hoverBorder: "hover:border-system-violet-500/60",
    accent: "text-system-violet-200",
    button:
      "bg-system-violet-500/20 hover:bg-system-violet-500/30 border-system-violet-400/30 hover:border-system-violet-400/50 text-system-violet-300",
    emptyBg:
      "from-system-violet-500/5 to-system-violet-600/3 border-system-violet-600/50",
    emptyIcon: "bg-system-violet-500/10 text-system-violet-400",
    tabActive:
      "text-system-violet-200 border-b-2 border-system-violet-400 bg-system-violet-500/10",
    tabHover: "hover:text-system-violet-200 hover:bg-system-violet-500/5",
  },
  "system-fuchsia": {
    bg: SHARED_BG,
    border: "border-system-fuchsia-600/40",
    hoverBg: SHARED_HOVER_BG,
    hoverBorder: "hover:border-system-fuchsia-500/60",
    accent: "text-system-fuchsia-200",
    button:
      "bg-system-fuchsia-500/20 hover:bg-system-fuchsia-500/30 border-system-fuchsia-400/30 hover:border-system-fuchsia-400/50 text-system-fuchsia-300",
    emptyBg:
      "from-system-fuchsia-500/5 to-system-fuchsia-600/3 border-system-fuchsia-600/50",
    emptyIcon: "bg-system-fuchsia-500/10 text-system-fuchsia-400",
    tabActive:
      "text-system-fuchsia-200 border-b-2 border-system-fuchsia-400 bg-system-fuchsia-500/10",
    tabHover: "hover:text-system-fuchsia-200 hover:bg-system-fuchsia-500/5",
  },
  // Custom-type families (valid theme families, distinct from predefined).
  "system-purple": {
    bg: SHARED_BG,
    border: "border-system-purple-500/40",
    hoverBg: SHARED_HOVER_BG,
    hoverBorder: "hover:border-system-purple-400/60",
    accent: "text-system-purple-200",
    button:
      "bg-system-purple-400/20 hover:bg-system-purple-400/30 border-system-purple-300/30 hover:border-system-purple-300/50 text-system-purple-200",
    emptyBg:
      "from-system-purple-400/5 to-system-purple-500/3 border-system-purple-500/50",
    emptyIcon: "bg-system-purple-400/10 text-system-purple-300",
    tabActive:
      "text-system-purple-200 border-b-2 border-system-purple-400 bg-system-purple-500/10",
    tabHover: "hover:text-system-purple-200 hover:bg-system-purple-500/5",
  },
  "system-matcha": {
    bg: SHARED_BG,
    border: "border-system-matcha-500/40",
    hoverBg: SHARED_HOVER_BG,
    hoverBorder: "hover:border-system-matcha-400/60",
    accent: "text-system-matcha-200",
    button:
      "bg-system-matcha-400/20 hover:bg-system-matcha-400/30 border-system-matcha-300/30 hover:border-system-matcha-300/50 text-system-matcha-200",
    emptyBg:
      "from-system-matcha-400/5 to-system-matcha-500/3 border-system-matcha-500/50",
    emptyIcon: "bg-system-matcha-400/10 text-system-matcha-300",
    tabActive:
      "text-system-matcha-200 border-b-2 border-system-matcha-400 bg-system-matcha-500/10",
    tabHover: "hover:text-system-matcha-200 hover:bg-system-matcha-500/5",
  },
  "system-salad": {
    bg: SHARED_BG,
    border: "border-system-salad-500/40",
    hoverBg: SHARED_HOVER_BG,
    hoverBorder: "hover:border-system-salad-400/60",
    accent: "text-system-salad-200",
    button:
      "bg-system-salad-400/20 hover:bg-system-salad-400/30 border-system-salad-300/30 hover:border-system-salad-300/50 text-system-salad-200",
    emptyBg:
      "from-system-salad-400/5 to-system-salad-500/3 border-system-salad-500/50",
    emptyIcon: "bg-system-salad-400/10 text-system-salad-300",
    tabActive:
      "text-system-salad-200 border-b-2 border-system-salad-400 bg-system-salad-500/10",
    tabHover: "hover:text-system-salad-200 hover:bg-system-salad-500/5",
  },
  "system-brand-pink": {
    bg: SHARED_BG,
    border: "border-system-brand-pink-500/40",
    hoverBg: SHARED_HOVER_BG,
    hoverBorder: "hover:border-system-brand-pink-400/60",
    accent: "text-system-brand-pink-200",
    button:
      "bg-system-brand-pink-400/20 hover:bg-system-brand-pink-400/30 border-system-brand-pink-300/30 hover:border-system-brand-pink-300/50 text-system-brand-pink-200",
    emptyBg:
      "from-system-brand-pink-400/5 to-system-brand-pink-500/3 border-system-brand-pink-500/50",
    emptyIcon: "bg-system-brand-pink-400/10 text-system-brand-pink-300",
    tabActive:
      "text-system-brand-pink-200 border-b-2 border-system-brand-pink-400 bg-system-brand-pink-500/10",
    tabHover:
      "hover:text-system-brand-pink-200 hover:bg-system-brand-pink-500/5",
  },
  "system-warning": {
    bg: SHARED_BG,
    border: "border-system-warning-500/40",
    hoverBg: SHARED_HOVER_BG,
    hoverBorder: "hover:border-system-warning-400/60",
    accent: "text-system-warning-200",
    button:
      "bg-system-warning-400/20 hover:bg-system-warning-400/30 border-system-warning-300/30 hover:border-system-warning-300/50 text-system-warning-200",
    emptyBg:
      "from-system-warning-400/5 to-system-warning-500/3 border-system-warning-500/50",
    emptyIcon: "bg-system-warning-400/10 text-system-warning-300",
    tabActive:
      "text-system-warning-200 border-b-2 border-system-warning-400 bg-system-warning-500/10",
    tabHover: "hover:text-system-warning-200 hover:bg-system-warning-500/5",
  },
}

/**
 * Color family used by each predefined initiative type. Custom types carry
 * their own family on the persisted record.
 */
export const PREDEFINED_TYPE_FAMILY: Record<InitiativeType, string> = {
  [InitiativeType.RETENTION]: "system-success",
  [InitiativeType.DEMAND_GENERATION]: "system-cyan",
  [InitiativeType.DEMAND_GENERATION_2]: "system-magenta",
  [InitiativeType.DEMAND_CAPTURE_ACQUISITION]: "system-sea",
  [InitiativeType.DEMAND_CAPTURE_RETARGETING]: "system-orange",
  [InitiativeType.DEMAND_CAPTURE_BROAD]: "system-yellow",
  [InitiativeType.BRANDED_SEARCH]: "system-indigo",
  [InitiativeType.NON_BRAND_SEARCH]: "system-violet",
  [InitiativeType.PMAX]: "system-fuchsia",
}

/** Fallback family for any unrecognized color value. */
export const FALLBACK_COLOR_FAMILY = "system-purple"

/** Single shared icon used for all custom initiative types. */
export const DEFAULT_CUSTOM_ICON = TagIcon

/**
 * Resolve a color family to its static ColorConfig, falling back to a default
 * family when the family is unknown (e.g. a legacy/removed custom color).
 */
export function getColorConfigForFamily(family: string | null): ColorConfig {
  if (family && INITIATIVE_TYPE_PALETTE[family]) {
    return INITIATIVE_TYPE_PALETTE[family]
  }
  return INITIATIVE_TYPE_PALETTE[FALLBACK_COLOR_FAMILY]
}
